import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { Icon } from '../../components/ui/Icon';
import { ErrorNote } from '../../components/ui/misc';
import { cn } from '../../lib/cn';
import { ApiError } from '../../lib/api/errors';
import { authApi } from './api';
import { AuthLayout } from './LoginPage';

// Mirrors `apps/api/src/auth/dto/auth.dto.ts`'s PASSWORD_MIN — the one rule
// the backend actually enforces. The other 3 checklist rows below are
// UX-only guidance (not a real policy requirement), so they never block
// submit — only length does, matching the server's real behavior exactly
// rather than a stricter client-invented one.
const PASSWORD_MIN = 12;

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const checks = {
    length: newPassword.length >= PASSWORD_MIN,
    case: /[a-z]/.test(newPassword) && /[A-Z]/.test(newPassword),
    digit: /\d/.test(newPassword),
    special: /[^A-Za-z0-9]/.test(newPassword),
  };
  const strength = Object.values(checks).filter(Boolean).length;

  if (!token) {
    return (
      <AuthLayout>
        <div className="mb-2 flex justify-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-alert-soft text-alert">
            <Icon name="link_off" className="text-[22px]" />
          </div>
        </div>
        <h1 className="text-center text-xl font-semibold text-ink">Reset link missing</h1>
        <p className="mt-2 text-center text-sm text-ink-muted">
          This page needs the token from your password reset email. If you followed a link here, it may be
          incomplete — request a new one below.
        </p>
        <div className="mt-6">
          <Button asChild className="w-full">
            <Link to="/forgot-password">Request a new reset link</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout>
        <div className="mb-2 flex justify-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-positive-soft text-positive">
            <Icon name="check_circle" className="text-[22px]" />
          </div>
        </div>
        <h1 className="text-center text-xl font-semibold text-ink">Password updated</h1>
        <p className="mt-2 text-center text-sm text-ink-muted">
          Your password has been changed and every other active session has been signed out. Sign in again with
          your new password.
        </p>
        <div className="mt-6">
          <Button asChild className="w-full">
            <Link to="/login">Continue to sign in</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError('The two passwords do not match.');
      return;
    }
    if (!checks.length) {
      setError(`Password must be at least ${PASSWORD_MIN} characters.`);
      return;
    }
    setBusy(true);
    try {
      await authApi.completePasswordReset(token, newPassword);
      setDone(true);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? 'This reset link is invalid or has expired. Request a new one and try again.'
          : 'Could not reach the server. Check your connection and try again.',
      );
      setBusy(false);
    }
  }

  return (
    <AuthLayout badge="Single-use verified recovery token">
      <h1 className="text-xl font-semibold text-ink">Set new password</h1>
      <p className="mt-1 text-sm text-ink-muted">Choose a new password for your account.</p>
      <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        <Field
          label="New password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          required
          icon={<Icon name="key" className="text-[18px]" />}
          labelExtra={<span className="text-[11px] text-ink-muted">Min. {PASSWORD_MIN} characters</span>}
          endAdornment={
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              // See LoginPage.tsx's identical toggle for why this avoids
              // the word "password" (Playwright getByLabel substring collision).
              aria-label={showPassword ? 'Hide typed characters' : 'Show typed characters'}
              className="px-2.5 text-ink-subtle transition-colors hover:text-ink"
            >
              <Icon name={showPassword ? 'visibility_off' : 'visibility'} className="text-[18px]" />
            </button>
          }
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />

        {newPassword.length > 0 && (
          <div className="rounded border border-line bg-surface-subtle p-3">
            <div className="flex items-center justify-between text-[11px]">
              <span className="font-medium text-ink-muted">Password strength</span>
              <span className={cn('font-semibold', STRENGTH_LABEL[strength].className)}>
                {STRENGTH_LABEL[strength].label}
              </span>
            </div>
            <div className="mt-2 grid grid-cols-4 gap-1.5">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className={cn('h-1.5 rounded-full', i < strength ? STRENGTH_LABEL[strength].bar : 'bg-sunken')} />
              ))}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1 text-[10.5px] text-ink-muted">
              <ChecklistRow ok={checks.length} label={`${PASSWORD_MIN}+ characters`} />
              <ChecklistRow ok={checks.case} label="Upper & lower case" />
              <ChecklistRow ok={checks.digit} label="Number included" />
              <ChecklistRow ok={checks.special} label="Special symbol" />
            </div>
          </div>
        )}

        <Field
          label="Confirm new password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          required
          icon={<Icon name="lock_reset" className="text-[18px]" />}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />

        <Button type="submit" loading={busy} className="mt-1 w-full">
          <span>Update password</span>
          <Icon name="arrow_forward" className="text-[18px]" />
        </Button>
      </form>
      <div className="mt-5 text-center">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary-hover">
          <Icon name="arrow_back" className="text-[15px]" />
          Return to sign in
        </Link>
      </div>
      <div className="mt-6 flex items-center justify-center border-t border-sunken pt-4 text-[11px] text-ink-muted">
        <span className="flex items-center gap-1.5 font-medium">
          <Icon name="shield_locked" className="text-[15px] text-positive" />
          Signs you out of every other active session
        </span>
      </div>
    </AuthLayout>
  );
}

const STRENGTH_LABEL: Record<number, { label: string; className: string; bar: string }> = {
  0: { label: 'Too weak', className: 'text-alert', bar: 'bg-alert' },
  1: { label: 'Weak', className: 'text-alert', bar: 'bg-alert' },
  2: { label: 'Fair', className: 'text-accent-fg', bar: 'bg-accent' },
  3: { label: 'Good', className: 'text-accent-fg', bar: 'bg-accent' },
  4: { label: 'Strong', className: 'text-positive', bar: 'bg-positive' },
};

function ChecklistRow({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={cn('flex items-center gap-1', ok ? 'text-positive' : 'text-ink-subtle')}>
      <Icon name={ok ? 'check' : 'remove'} className="text-[14px]" />
      {label}
    </span>
  );
}
